import { BranchCode } from "api/risk";
import { ValidationTracker } from "components/domain/ValidationTracker/ValidationTracker";
import { FormChangeTracker } from "components/domain/FormChangeTracker";
import { Form, Formik, FormikProps } from "formik";
import { FC, useEffect, useMemo, useRef } from "react";
import { useAppDispatch } from "app/hooks";
import { resetProfiling } from "features/subscription-process/profiling-process/slices/genericProfilingSlice";
import { useRealEstateForm } from "../../hooks/useRealEstateForm";
import { useRealEstateFormRef } from "../../hooks/useRealEstateFormRef";
import { RealEstateFormValues } from "../../types/realEstateFormValues";
import { RealEstateLayout } from "./RealEstateLayout";

interface RealEstateFormProps {
  realEstateId?: string;
  riskId?: string;
  parcId?: string;
  refreshRealEstates?: () => void;
  branchCode: BranchCode
  formikRef?: React.RefObject<FormikProps<RealEstateFormValues>>
  onValidationChange?: (isValid: boolean) => void;
}

export const RealEstateForm: FC<RealEstateFormProps> = ({
  realEstateId,
  riskId,
  parcId,
  refreshRealEstates,
  branchCode,
  formikRef,
  onValidationChange
}) => {
  const dispatch = useAppDispatch();
  const mountIdRef = useRef(Date.now());
  const { formikRef: formikRefHook, isFormValid, handleValidationChange, hasFormChanges, handleFormChange } = useRealEstateFormRef()

  const currentFormikRef = formikRef || formikRefHook

  // Reset survey state on mount
  useEffect(() => {
    dispatch(resetProfiling());
  }, [dispatch]);

  const {
    initialValues,
    submitRealEstate,
    validationSchema,
    realEstate
  } = useRealEstateForm({
    realEstateId,
    refreshRealEstates,
    branchCode,
    formikRef: currentFormikRef,
    isFormValid,
    hasFormChanges,
    riskId,
    parcId
  });

  const formKey = useMemo(() => {
    if (realEstate)
      return realEstate.id
    return `new-${mountIdRef.current}`
  }, [realEstate])

  const currentValidationChange = useMemo(() => {
    if (onValidationChange)
      return onValidationChange
    return handleValidationChange
  }, [handleValidationChange, onValidationChange])

  return (
    <Formik
      key={formKey}
      innerRef={currentFormikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={submitRealEstate}
    >
      {
        ({ isValid, values }) => (
          <>
            <ValidationTracker isValid={isValid} onValidationChange={currentValidationChange} />
            <FormChangeTracker<RealEstateFormValues>
              values={values}
              initialValues={initialValues}
              onFormChange={handleFormChange}
            />
            <Form>
              <RealEstateLayout branchCode={branchCode} />
            </Form>
          </>
        )
      }
    </Formik>
  );
};
